import React, { useContext, useMemo, useEffect, useState } from 'react';
import { useCategoryDetailsQuery } from '../../../gql/hooks';
import { useTranslations } from '../../../hooks/translations';
import { PageContainer, PageContent, PaginationController } from 'app/components/common';
import { CategoryUtils } from '../../../utils';
import { DEFAULT_CATEGORY_BANNER_PLACEHOLDER_IMAGE, SEIKO_DEFAULT_CATEGORY_BANNER_PLACEHOLDER_IMAGE } from '../../../const';
import { IS_SEIKO_THEME } from '../../../branding';
import { InfoBanner } from '../../common/InfoBanner';
import { BreadcrumbContext } from '../../../breadcrumbs/BreadcrumbContext';
import { Button, Dropdown, Menu, Spin } from 'antd';
import classNames from 'classnames';
import { CaretDownFilled, CaretUpFilled, OrderedListOutlined } from '@ant-design/icons';
import { getParams, setParams, setSearchParam } from '../../../branding/utils/urlManipulation';
import { Translate } from '../../../services/translationService/components/Translate';
import LearningList from '../../common/learningList';
import { useSearchLazyQuery } from '../../../gql/hooks/queries/useSearchLazyQuery';
import { AUTH_ENV } from '../../../environments';
import './styles.less';

const keys = {
    courses: 'courses',
    curriculum: 'curricula',
    grid: 'card',
    list: 'list',
    asc: 'asc',
    dec: 'dec'
};

const courseSorts = [ {
    key: 'atoz',
    sortObject: [ { sort: 'name', order: 'asc' } ],
}, {
    key: 'ztoa',
    sortObject: [ { sort: 'name', order: 'desc' } ],
} ];

const currSorts = [ {
    key: 'weight',
    sortObject: [ { sort: 'weight', order: 'asc' }, { sort: 'name', order: 'asc' } ],
}, ...courseSorts ];

export const CategoryDetailsPage = ( props ) => {

    const initialUrlParams = getParams();

    const categoryId = useMemo( () => props?.route?.params?.categoryId, [ props ] );
    const { locale: activeLocale } = useTranslations();

    const { leaveCrumb } = useContext( BreadcrumbContext );    
    
    const { data: categoryDataResponse, loading: categoryLoading } = useCategoryDetailsQuery( categoryId );
     
    const imageData = useMemo( () => {
        let catId = '';
        
        if ( AUTH_ENV === 'qa' ) {
            catId = '861ea2ff-90ea-46fb-8d0d-07022dd5c5db';
        } else 
            if ( AUTH_ENV === 'pprd' ) {
                catId = '78f74d09-89d0-4e90-bdd7-4a7d2d0206a6';
            } else 
                if ( AUTH_ENV === 'prod' ) {           
                    catId = 'a143d556-3f68-4f82-b933-18e3a8cd285d';
                } 

        if ( catId === props.route.params.categoryId ) {
            return {
                backgroundSrc: SEIKO_DEFAULT_CATEGORY_BANNER_PLACEHOLDER_IMAGE,
                additionalClass: 'seiko-special'
            };
        } else {
            return {
                backgroundSrc: IS_SEIKO_THEME ? SEIKO_DEFAULT_CATEGORY_BANNER_PLACEHOLDER_IMAGE : undefined,
                imageSrc: !IS_SEIKO_THEME ? DEFAULT_CATEGORY_BANNER_PLACEHOLDER_IMAGE : undefined
            };
        }
    }, [ props ] );
   
    /**
     * The name of the category to display on the site. This should already be in the
     * language currently active on the site.
     * @type { string }
     */
    const categoryName = useMemo( () => {
        if ( categoryLoading ) {
            return '';
        }
        const name = CategoryUtils.getName( categoryDataResponse, { localeCode: activeLocale } );
        leaveCrumb( props.pageKey, name );
        return name;
    }, [ categoryDataResponse, activeLocale ] );

    /**
     * The description of the category to display on the site. This should already be in
     * the language currently active on the site.
     * @type { string }
     */
    const categoryDescription = useMemo( () => {
        return CategoryUtils.getDescription( categoryDataResponse, { localeCode: activeLocale } );
    }, [ categoryDataResponse, activeLocale ] );

    function getSort( key, filter ) {
        const list = filter == keys.curriculum ? currSorts : courseSorts;
        const newSort = list.find( l => l.key == key ) ?? list[ 0 ];
        return newSort;
    }

    function toggleSort( key, filter ) {
        const list = filter == keys.curriculum ? currSorts : courseSorts;
        const index = list.findIndex( l => l.key == key );
        const newSort = index === null || index == list.length - 1 ? list[ 0 ] : list[ index + 1 ];
        setSortKey( newSort.key );
        return newSort;
    }
    
    const [ pageData, setPageData ] = useState( initialUrlParams.page ? { page: parseInt( initialUrlParams.page ) } : { page: 1 } );
    // these store the keys, not the values
    const [ filterInfo, setFilterInfo ] = useState( initialUrlParams.filter ?? keys.curriculum ); 
    const [ sortKey, setSortKey ] = useState( initialUrlParams.sort ?? 'weight' );
    const [ view, setView ] = useState( initialUrlParams.view ?? keys.grid );

    const [ searchQuery, { data, loading: queryLoading, error, pageData: searchPageData } ] = useSearchLazyQuery( {}, false, false );
    const query = async ( page, sortObject, filter ) => {
        searchQuery( {
            types: [ filter ],
            query: '',
            page: page ?? 1,
            limit: 8, 
            sortObject,
            categoryId,
        } );
    };

    const [ paginationController, setPaginationController ] = useState();

    function fetch( { pageNumber, sortKey = sortKey, filter = filterInfo } ) {

        const sort = getSort( sortKey, filter );
        const params = {
            page: pageNumber ?? pageData?.page,
            sort: sort.key,
            filter: filter
        };

        setParams( params );
        
        query( params.page, sort.sortObject, filter );
    }

    useEffect( () => {
        fetch( { } );
    }, [ activeLocale, props ] );

    function onPageChange( page ) {

        setPageData( { ...pageData, page } );
        fetch( { pageNumber: page } );
    }

    function onFilterChange( filter ) {

        paginationController?.setActivePage( 0 );
        setFilterInfo( filter );
        const sort = getSort( sortKey, filter );
        setSortKey( sort.key, filter );
        fetch( { filter, pageNumber: 1 } );
    }

    function selectFilter( opt ) {
        if ( filterInfo !== opt ) {
            onFilterChange( opt );
        }
    }

    function onSortChange( sortKey, filterInfo ) {

        paginationController?.setActivePage( 0 );
        const newSort = toggleSort( sortKey, filterInfo );
        fetch( { sortKey: newSort.key, pageNumber: 1 } );
    }

    function onViewChange( viewInfo ) {
        setView( viewInfo );
        setSearchParam( 'view', viewInfo );
    }

    useEffect( () => {
        if ( searchPageData.totalResults !== undefined && !queryLoading ) {

            setPageData( searchPageData );
        }
    }, [ searchPageData, queryLoading ] );

    return (
        <PageContent className="page-category-details">           
           <PageContainer className="category-details-banner-container">
                
                <InfoBanner
                    title={ categoryName }
                    { ...imageData }
                    type='category' />

                <div className='learning-content'>

                    <h4>
                        <Translate.Text className='title' value="application.windowTitle.categoryDetails" />
                    </h4>
                    <div className='_body'>
                        <span dangerouslySetInnerHTML={ { __html: categoryDescription } } />
                    </div>

                    <div className='divider' />

                    <div className='controls'>
                        <div className='filter-controls non-compact'>

                                <Button shape='round' key={ keys.courses } onClick={ () => selectFilter( keys.courses ) } className={ classNames( { active: filterInfo == keys.courses }, 'control' ) }>
                                    <Translate.Text value='control.courses'/>
                                </Button> 

                                <Button shape='round' key={ keys.curriculum } onClick={ () => selectFilter( keys.curriculum ) } className={ classNames( { active: filterInfo == keys.curriculum }, 'control' ) }>
                                    <Translate.Text value='control.curriculum'/>
                                </Button> 
                        </div>
                        <div className='filter-controls compact'>
                            <Dropdown 
                                trigger={ [ 'click' ] }
                                overlay={
                                    <Menu>
                                        <Menu.Item key={ keys.courses } 
                                            className={ classNames( 'selectable', { selected: ( filterInfo == keys.courses ) } ) }
                                            onClick={ () => selectFilter( keys.courses ) } >
                                            <Translate.Text value='control.courses'/>
                                        </Menu.Item>
                                        <Menu.Item key={ keys.curriculum } 
                                            className={ classNames( 'selectable', { selected: ( filterInfo == keys.curriculum ) } ) }
                                            onClick={ () => selectFilter( keys.curriculum ) } >
                                            <Translate.Text value='control.curriculum'/>
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                    <span className='button-text'>
                                        { filterInfo == keys.courses ? <Translate.Text value='control.courses'/> : <Translate.Text value='control.curriculum'/> }
                                        <CaretDownFilled />
                                    </span>
                                </Button>
                                    
                            </Dropdown>
                        </div>

                        <div className='sort-controls'>
                            <Button shape='round' onClick={ () => onSortChange( sortKey, filterInfo ) } className={ 'active control' } >
                            { sortKey === 'atoz' &&
                                <><Translate.Text value="control.alphabetical" /><CaretDownFilled /></>
                            }
                            
                            { sortKey === 'ztoa' &&
                                <><Translate.Text value="control.antiAlphabetical" /><CaretUpFilled /></> 
                            }
                            
                            { ( filterInfo === 'curricula' && sortKey === 'weight' ) && 
                                <><OrderedListOutlined style={ { fontSize: 'xx-large' } }/><CaretDownFilled /></>
                            }
                            </Button> 
                        </div>

                        <div className='view-controls compact desktop-only'>
                            <Dropdown 
                                trigger={ [ 'click' ] }
                                overlay={
                                    <Menu>
                                        <Menu.Item key={ keys.list } 
                                            className={ classNames( 'selectable', { selected: ( view == keys.list ) } ) }
                                            onClick={ () => onViewChange( keys.list ) } >
                                            <Translate.Text value='courseList.viewOptions.list.label'/>
                                        </Menu.Item>
                                        <Menu.Item key={ keys.grid } 
                                            className={ classNames( 'selectable', { selected: ( view == keys.grid ) } ) }
                                            onClick={ () => onViewChange( keys.grid ) } >
                                            <Translate.Text value='courseList.viewOptions.grid.label'/>
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                    <span className='button-text'>
                                        { view == keys.grid ? <Translate.Text value='courseList.viewOptions.grid.label'/> : <Translate.Text value='courseList.viewOptions.list.label'/> }
                                        <CaretDownFilled />
                                    </span>
                                </Button>
                                    
                            </Dropdown>
                        </div>
                        <div className='view-controls non-compact desktop-only'>

                            <Button shape='round' key={ keys.list } onClick={ () => onViewChange( keys.list ) } className={ classNames( { active: view == keys.list }, 'control' ) }>
                                <Translate.Text value='courseList.viewOptions.list.label'/>
                            </Button> 

                            <Button shape='round' key={ keys.grid } onClick={ () => onViewChange( keys.grid ) } className={ classNames( { active: view == keys.grid }, 'control' ) }>
                                <Translate.Text value='courseList.viewOptions.grid.label'/>
                            </Button> 
                        </div>

                    </div>
                    <div className={ classNames( 'search-results', { empty: data?.length === 0 && error } ) }>
                        {
                            queryLoading || data == null ? <Spin /> : 
                                data?.length === 0 && error ? <div className='empty-page no-border'><Translate.Text className='title' value='page.searchLibrary.empty' /></div> : // nothing is returned from search
                                <LearningList
                                    displayType={ view }
                                    learningType={ filterInfo == keys.courses ? 'course' : 'curriculum' }
                                    data={ data }
                                    isLoading={ queryLoading }
                                    />
                        }
                    </div>
                </div>

                <div className={ classNames( 'learning-list-pagination-controller', { hidden: data?.length === 0 || error } ) }>
                    { pageData?.totalResults > 0 &&
                        <PaginationController
                            pageSize={ pageData.limit }
                            initialPage={ pageData.page - 1 }
                            itemCount={ pageData.totalResults }
                            onChange={ ( index ) => onPageChange( index + 1 ) }
                            getController={ setPaginationController }
                        />
                    }
                        
                </div>
            </PageContainer>
        </PageContent>
    );
};
